import { useCountryStateOptions, useFacility, useProfile } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import type { FacilityAttributes, LetterheadLayout, SignatureLayout } from '@type/facility.type'
import { FormButton, FormInputField, Modal, ReportBrandingControls } from '@ui'
import { FormSelect, Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { queryClient } from '@store/provider/TanStackProvider'
import { useProfile as useProfileStore } from '@zustore/store'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

const selectOption = (label: string, value: string) => ({ label, value })
const FACILITY_TYPE_OPTIONS = [selectOption('Hospital', 'hospital'), selectOption('Clinic', 'clinic')]
const STATUS_OPTIONS = [selectOption('Active', 'active'), selectOption('Inactive', 'inactive')]

const addFacilitySchema = yup.object({
  address_line1: yup.string().required('Address is required'),
  city: yup.string().required('Please select a city'),
  country: yup.string().required('Please select a country'),
  facility_type: yup.string().required('Please select a type'),
  name: yup.string().required('Facility name is required'),
  status: yup.string(),
  state: yup.string().required('Please select a state'),
  letterhead_layout: yup.mixed<LetterheadLayout>().oneOf(['logo_left', 'logo_right', 'stacked']).required(),
  signature_layout: yup.mixed<SignatureLayout>().oneOf(['left', 'right', 'center']).required()
})

export type AddFacilityFormValues = yup.InferType<typeof addFacilitySchema>

type Props = {
  onClose: () => void
  id?: string
}

const AddFacility = ({ onClose, id }: Props) => {
  const { createFacility, singleFacility, updateFacility } = useFacility({ id })
  const { facilityId: profileFacilityId } = useProfile({ isLoggedIn: true })
  const { facility: storedFacility, setFacility } = useProfileStore()

  const methods = useForm<AddFacilityFormValues>({
    defaultValues: {
      address_line1: '',
      city: '',
      country: '',
      facility_type: '',
      name: '',
      status: 'active',
      letterhead_layout: 'logo_left',
      signature_layout: 'right'
    },
    resolver: yupResolver(addFacilitySchema)
  })
  const { control, handleSubmit, formState, watch, setValue } = methods
  const country = watch('country')
  const letterheadLayout = watch('letterhead_layout')
  const signatureLayout = watch('signature_layout')
  const { countries, states } = useCountryStateOptions(country)

  useEffect(() => {
    if (!id || !singleFacility) return
    methods.reset(singleFacility.attributes)
    methods.setValue('country', singleFacility.attributes.location.address.country)
    methods.setValue('state', singleFacility.attributes.location.address.state)
    methods.setValue('city', singleFacility.attributes.location.address.city)
    methods.setValue('address_line1', singleFacility.attributes.location.address.line1)
    methods.setValue('name', singleFacility.attributes.name)
    methods.setValue('facility_type', singleFacility.attributes.facility_type)
    methods.setValue('status', singleFacility.attributes.status)
    methods.setValue('letterhead_layout', singleFacility.attributes.report_branding?.letterhead_layout ?? 'logo_left')
    methods.setValue('signature_layout', singleFacility.attributes.report_branding?.signature_layout ?? 'right')
  }, [id, singleFacility, methods])

  const addressLine = watch('address_line1') ?? ''

  const openGoogleMapsForAddress = () => {
    const q = addressLine.trim() || 'hospitals near me'
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  const onSubmit = async (values: AddFacilityFormValues) => {
    const statusLabel = values.status?.toLowerCase() === 'active' ? 'active' : 'inactive'

    const body = {
      data: {
        ...(id ? { id } : {}),
        attributes: {
          facility_type: values.facility_type,
          location: {
            address: {
              city: values.city,
              country: values.country,
              line1: values.address_line1,
              line2: '',
              postal_code: '',
              state: values.state
            }
          },
          name: values.name,
          status: statusLabel,
          report_branding: {
            letterhead_layout: values.letterhead_layout,
            signature_layout: values.signature_layout
          }
        },
        type: 'facility'
      }
    }

    try {
      id ? await updateFacility(body) : await createFacility(body)
      // Branding is per facility record (e.g. Seed Campus), not REACT_APP_HOSPITAL_NAME.
      const isCurrentStaffFacility =
        Boolean(id) &&
        (String(id) === String(profileFacilityId) ||
          (storedFacility?.name != null && storedFacility.name === values.name))
      if (isCurrentStaffFacility) {
        const base = storedFacility ?? singleFacility?.attributes
        if (base) {
          setFacility({
            ...base,
            facility_type: body.data.attributes.facility_type,
            location: {
              ...base.location,
              address: {
                ...base.location?.address,
                ...body.data.attributes.location.address
              }
            },
            name: body.data.attributes.name,
            status: body.data.attributes.status,
            report_branding: body.data.attributes.report_branding
          } as FacilityAttributes)
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['profile'] })
      void queryClient.invalidateQueries({ queryKey: ['facilities'] })
      showToast(id ? 'Facility updated successfully' : 'Facility added successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal
        onClose={onClose}
        style={{ minWidth: 'min(520px, calc(100vw - 1.5rem))' }}
        title={id ? 'Edit Facility' : 'Add Facility'}
      >
        <form className="flex min-w-[480px] flex-col overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-5 overflow-auto px-8 py-4">
            <div className="">
              <FormInputField label="Facility Name" name="name" placeholder="Enter facility name" />
            </div>
            <div className="">
              <Controller
                control={control}
                name="facility_type"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    label="Type"
                    name="facility_type"
                    onChange={(val: any) => onChange(val?.value)}
                    options={FACILITY_TYPE_OPTIONS}
                    placeholder="Select facility type"
                    value={FACILITY_TYPE_OPTIONS.find(({ value: val }) => val === value)}
                  />
                )}
              />
            </div>

            <div>
              <div className="mb-[2px] flex items-center justify-between gap-2">
                <p className="text-sm font-medium">Address</p>
                <button
                  className="text-sm font-medium text-[#2d7a4f] hover:underline hidden"
                  onClick={openGoogleMapsForAddress}
                  type="button"
                >
                  Use google location
                </button>
              </div>
              <FormInputField name="address_line1" placeholder="Street address" />
            </div>

            <Controller
              control={control}
              name="country"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  borderRadius={'100px'}
                  error={error?.message}
                  label="Country"
                  name="country"
                  onChange={(val: any) => {
                    onChange(val?.value)
                  }}
                  options={countries}
                  placeholder="Select a country"
                  value={countries.find(({ value: val }) => val === value)}
                />
              )}
            />

            <Controller
              control={control}
              name="state"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  borderRadius={'100px'}
                  error={error?.message}
                  label="State"
                  name="state"
                  onChange={(val: any) => {
                    onChange(val?.value)
                  }}
                  options={states}
                  placeholder="Select state"
                  value={states.find(({ value: val }) => val === value)}
                />
              )}
            />
            <FormInputField label="City" name="city" placeholder="City" />
            <FormSelect
              borderRadius={'100px'}
              label="Status"
              name="status"
              options={STATUS_OPTIONS}
              placeholder="Select status"
            />

            <div className="border-t border-[#eee] pt-4">
              <p className="mb-1 text-sm font-semibold text-[#222]">Report branding</p>
              <ReportBrandingControls
                description="Layouts are stored on this facility (e.g. Seed Campus), not the hospital display name. They apply to lab/imaging results for staff assigned to this facility. Logo and stamp images still come from the app environment."
                onChange={next => {
                  setValue('letterhead_layout', next.letterhead_layout, { shouldDirty: true })
                  setValue('signature_layout', next.signature_layout, { shouldDirty: true })
                }}
                value={{
                  letterhead_layout: letterheadLayout,
                  signature_layout: signatureLayout
                }}
              />
            </div>
          </div>

          <FormButton isLoading={formState.isSubmitting} onClose={onClose} submit={id ? 'Update' : 'Add'} />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default AddFacility
